import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Monitor } from "lucide-react";
import { useHeaderScroll } from "@/hooks";
import { Link, useLocation } from "react-router-dom";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useTheme } from "next-themes";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isScrolled, isVisible, backgroundColor } = useHeaderScroll(80);
  const location = useLocation();
  const { theme } = useTheme();

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  }, [isMobileMenuOpen]);

  const handleNavClick = useCallback((href: string) => {
    // Se for uma âncora interna na página home, faz scroll
    if (href.startsWith('#') && location.pathname === '/') {
      const element = document.querySelector(href);
      if (element) {
        const headerHeight = 64;
        const elementPosition = (element as HTMLElement).offsetTop - headerHeight;
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { label: "Home", href: "/", isRoute: true },
    { label: "Sobre", href: "/sobre", isRoute: true },
    { label: "Serviços", href: "/servicos", isRoute: true },
    { label: "Consultoria IA", href: "/consultoria-ia", isRoute: true },
    { label: "Flows", href: "/flows", isRoute: true },
    { label: "Contato", href: "/contato", isRoute: true },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-all duration-500 ${
        isScrolled
          ? 'bg-background/95 dark:bg-fs-dark-950/95 border-border shadow-lg shadow-primary/10'
          : 'bg-background/30 dark:bg-fs-dark-950/30 border-transparent'
      } ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
      role="banner"
      aria-label="Navegação principal"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <img
                src={theme === 'dark' ? '/logo-dark.png' : '/logo-light.png'}
                alt="Future Station"
                className="h-8 sm:h-10 md:h-12 w-auto hover:scale-105 transition-transform duration-300"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-8">
            {menuItems.map((item) => (
              item.isRoute ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`text-foreground/90 hover:text-fs-cyan-500 font-medium transition-all duration-300 hover:fs-gradient-text hover:scale-105 relative group ${
                    location.pathname === item.href ? 'fs-gradient-text' : ''
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 transition-all duration-300 group-hover:w-full ${
                    location.pathname === item.href ? 'w-full' : 'w-0'
                  }`} />
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.href)}
                  className="text-foreground/90 hover:text-fs-cyan-500 font-medium transition-all duration-300 hover:fs-gradient-text hover:scale-105 relative group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 transition-all duration-300 group-hover:w-full" />
                </button>
              )
            ))}
          </nav>

          {/* Desktop CTA Button and Theme Toggle */}
          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle />
            <Button 
              variant="fs-primary" 
              size="lg"
              onClick={() => window.open('http://primeclub.futurestation.com.br:8080/portal/home', '_blank')}
            >
              <Monitor className="w-4 h-4 mr-2" />
              Ambiente de Demonstração
            </Button>
          </div>

          {/* Mobile Menu Button and Theme Toggle */}
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              className="text-foreground/90 p-2"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden overflow-y-auto overflow-x-hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'max-h-[calc(100vh-80px)] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="mt-4 pb-4 border-t border-border">
            <nav className="flex flex-col space-y-4 mt-4">
              {menuItems.map((item, index) => (
                item.isRoute ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-foreground/90 hover:text-fs-cyan-500 font-medium transition-all duration-300 hover:fs-gradient-text py-2 text-left transform ${
                      isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                    } ${location.pathname === item.href ? 'fs-gradient-text' : ''}`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item.href)}
                    className={`text-foreground/90 hover:text-fs-cyan-500 font-medium transition-all duration-300 hover:fs-gradient-text py-2 text-left transform ${
                      isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                    }`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    {item.label}
                  </button>
                )
              ))}
              <div className={`pt-4 border-t border-border transform transition-all duration-300 ${
                isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`} style={{ transitionDelay: '300ms' }}>
                <Button 
                  variant="fs-primary" 
                  size="lg" 
                  className="w-full"
                  onClick={() => window.open('http://primeclub.futurestation.com.br:8080/portal/home', '_blank')}
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Ambiente de Demonstração
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;